import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogOverlay, DialogPortal } from '@/components/ui/dialog';
import { FileUploadTabs } from '@/components/FileUploadTabs';
import { Progress } from '@/components/ui/progress';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ApiTranscriptionData } from '@/types/sections';
import { useAuth } from '@/contexts/AuthContext';
import { processAnalysis } from '@/config/autoGeneration';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';

interface FileUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTranscriptionComplete?: (transcriptions: ApiTranscriptionData[], title: string, analysisId: number) => void;
  template?: any;
  onRefresh?: () => void;
}

export const FileUploadModal = ({ 
  open, 
  onOpenChange, 
  onTranscriptionComplete,
  template,
  onRefresh
}: FileUploadModalProps) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<'idle' | 'processing' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  // Get language code for Deepgram (same logic as useDeepgram.ts)
  const getDeepgramLanguage = () => {
    const currentLang = i18n.language;
    const baseLang = currentLang.split('-')[0].toLowerCase();
    
    switch (baseLang) {
      case 'es':
        return 'es';
      case 'pt':
        return 'pt';
      case 'en':
        return 'en';
      default:
        return 'es';
    }
  };

  const handleFileSelect = async (file: File) => {
    if (!template || !user) {
      console.error('❌ No template or user available');
      setStatus('error');
      setStatusMessage(t('error_processing_file'));
      return;
    }

    try {
      setIsProcessing(true);
      setStatus('processing');
      setProgress(10);
      setStatusMessage(t('creating_analysis'));

      // Get current language for API calls
      const currentLanguage = getDeepgramLanguage();

      // Step 1: Create analysis
      const iaReport = Object.fromEntries(
        template.attributes.sections.map((section: any) => [section.key, "registrar"])
      );

      const response = await axios.post(`${API_BASE_URL}/analyses`, {
        data: {
          name: "Nuevo análisis",
          doctor: user.id,
          template: template.id,
          iaReport: iaReport,
          transcription: [],
          diffs: [],
          archived: false,
          start: new Date().toISOString(),
          email: user.email,
          notes: [],
        },
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      const analysisId = response.data.data.id;
      console.log('📄 UPLOAD: Analysis created with ID:', analysisId);
      setProgress(20);
      setStatusMessage(t('uploading_file'));

      // Step 2: Upload file to S3
      const uploadResponse = await fetch(`${API_BASE_URL}/get-presigned-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filename: file.name,
          filetype: file.type,
        }),
      });

      const uploadData = await uploadResponse.json();
      setProgress(30);
      setStatusMessage(t('processing_file'));

      // Upload to S3
      await fetch(uploadData.url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      setProgress(50);
      setStatusMessage(t('transcribing_audio'));

      // Step 3: Process with Deepgram using dynamic language
      const deepgramResponse = await fetch(`https://api.deepgram.com/v1/listen?model=nova-2&language=${currentLanguage}&smart_format=true&punctuate=true&diarize=true&utterances=false&paragraphs=true`, {
        method: 'POST',
        headers: {
          'Authorization': 'Token 1910351be598c5f40909cf907e6a0b3e678538b8',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          url: uploadData.url.split('?')[0]
        }),
      });

      const deepgramData = await deepgramResponse.json();
      setProgress(70);
      setStatusMessage(t('processing'));

      // Step 4: Process transcriptions
      const segments = deepgramData.results?.channels?.[0]?.alternatives?.[0]?.words || [];
      console.log('📄 UPLOAD: Deepgram processing complete:', segments.length, 'segments');

      const transcriptions: ApiTranscriptionData[] = [];
      const groupedSegments = [];
      let currentGroup = [];
      let currentSpeaker = segments[0]?.speaker;
      let currentStartTime = segments[0]?.start || 0;

      for (const segment of segments) {
        if (segment.speaker === currentSpeaker && currentGroup.length < 10) {
          currentGroup.push(segment.punctuated_word || segment.word);
        } else {
          if (currentGroup.length > 0) {
            groupedSegments.push({
              speaker: currentSpeaker,
              words: currentGroup.join(' '),
              startTime: currentStartTime
            });
          }
          currentGroup = [segment.punctuated_word || segment.word];
          currentSpeaker = segment.speaker;
          currentStartTime = segment.start;
        }
      }

      if (currentGroup.length > 0) {
        groupedSegments.push({
          speaker: currentSpeaker,
          words: currentGroup.join(' '),
          startTime: currentStartTime
        });
      }

      for (const group of groupedSegments) {
        const minutes = Math.floor(group.startTime / 60);
        const seconds = Math.floor(group.startTime % 60);
        const timeString = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

        transcriptions.push({
          speakerId: group.speaker.toString(),
          recordTime: timeString,
          transcript: group.words,
          speakerType: `Hablante ${group.speaker}`
        });
      }

      setProgress(80);
      setStatusMessage(t('processing'));

      // Step 5: Update analysis with transcriptions
      await axios.put(`${API_BASE_URL}/analyses/${analysisId}`, {
        data: {
          transcription: transcriptions
        }
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('📄 UPLOAD: Analysis updated with transcriptions');
      setProgress(85);
      setStatusMessage(t('processing'));

      // Step 6: Generate AI report using dynamic language
      let aiReportGenerated = false;
      try {
        const jwt = localStorage.getItem('token');
        if (jwt && user.email) {
          console.log('📄 UPLOAD: Starting AI report generation...');
          
          const aiResponse = await processAnalysis(
            analysisId.toString(),
            currentLanguage, // Use dynamic language instead of hardcoded 'es'
            jwt,
            user.email
          );

          if (aiResponse.answer) {
            try {
              const parsedContent = JSON.parse(aiResponse.answer);
              console.log('📄 UPLOAD: AI report generated successfully:', Object.keys(parsedContent));
              
              // Update analysis with AI-generated content
              await axios.put(`${API_BASE_URL}/analyses/${analysisId}`, {
                data: {
                  iaReport: parsedContent
                }
              }, {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`,
                  'Content-Type': 'application/json',
                },
              });
              console.log('📄 UPLOAD: Analysis updated with AI content');
              aiReportGenerated = true;
              
            } catch (parseError) {
              console.error('❌ Error parsing AI response:', parseError);
            }
          }
        }
      } catch (aiError) {
        console.error('❌ Error generating AI report:', aiError);
      }

      setProgress(95);
      setStatusMessage(t('processing'));

      // Step 7: Generate title using dynamic language
      let finalTitle = "Nuevo análisis";
      try {
        const fullTranscript = transcriptions.map(t => t.transcript).join(' ');
        if (fullTranscript.length > 50) {
          const { generateTitle } = await import('@/config/autoGeneration');
          const jwt = localStorage.getItem('token');
          
          if (jwt && user.email) {
            const titleResponse = await generateTitle(currentLanguage, fullTranscript, jwt, user.email); // Use dynamic language
            
            if (titleResponse.answer) {
              finalTitle = titleResponse.answer;
              await axios.put(`${API_BASE_URL}/analyses/${analysisId}`, {
                data: {
                  subject: titleResponse.answer
                }
              }, {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`,
                  'Content-Type': 'application/json',
                },
              });
              console.log('📄 UPLOAD: Analysis updated with title in subject field:', titleResponse.answer);
            }
          }
        }
      } catch (titleError) {
        console.error('❌ Error generating title:', titleError);
        // Don't fail the entire process if title generation fails
      }

      // Step 8: Mark analysis as finished with proper end date
      const endDate = new Date().toISOString();
      try {
        await axios.put(`${API_BASE_URL}/analyses/${analysisId}`, {
          data: {
            end: endDate,
            finished: true
          }
        }, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        });
        console.log('📄 UPLOAD: Analysis marked as finished with end date:', endDate);
      } catch (finishError) {
        console.error('❌ Error marking analysis as finished:', finishError);
      }

      setProgress(100);
      setStatusMessage(t('processing'));

      // Call the completion callback with the final title
      onTranscriptionComplete?.(transcriptions, finalTitle, analysisId);

      // Refresh the analysis list to show the updated title
      onRefresh?.();

      // Close modal automatically after successful processing
      setTimeout(() => {
        onOpenChange(false);
        // Reset state when closing
        setStatus('idle');
        setProgress(0);
        setStatusMessage('');
        setIsProcessing(false);
      }, 500);

    } catch (error) {
      console.error('❌ Error in file upload process:', error);
      setStatus('error');
      setStatusMessage(t('error_processing_file'));
    } finally {
      if (status !== 'error') {
        setIsProcessing(false);
      }
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      onOpenChange(false);
      // Reset state when closing
      setTimeout(() => {
        setStatus('idle');
        setProgress(0);
        setStatusMessage('');
      }, 300);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogPortal>
        {/* Custom overlay with much higher z-index */}
        <div className="fixed inset-0 z-[9999] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        
        {/* Custom content with much higher z-index */}
        <div className="fixed left-[50%] top-[50%] z-[9999] grid w-full max-w-md translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg">
          
          <DialogHeader>
            <DialogTitle>{t('upload_file')}</DialogTitle>
          </DialogHeader>

          {status === 'idle' && (
            <FileUploadTabs 
              onFileSelect={handleFileSelect}
            />
          )}

          {status === 'processing' && (
            <div className="space-y-4 py-6">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
              <div className="space-y-2">
                <Progress value={progress} className="w-full" />
                <p className="text-sm text-muted-foreground text-center">
                  {statusMessage}
                </p>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4 py-6 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
              <div>
                <h3 className="text-lg font-medium">{t('error')}</h3>
                <p className="text-sm text-muted-foreground">
                  {statusMessage}
                </p>
              </div>
              <Button onClick={handleClose} variant="outline" className="w-full">
                {t('cancel')}
              </Button>
            </div>
          )}

          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span className="sr-only">Close</span>
          </button>
        </div>
      </DialogPortal>
    </Dialog>
  );
};
